import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { httpsCallable } from 'firebase/functions'
import { firebase_functions } from '../../config/firebaseConfig'
import {
  pageContainer,
  pageHeader,
  pageTitle,
  pageSubtitle,
  sectionCard,
  sectionHeader,
  sectionTitle,
  tableHeadCell,
  tableBodyCell,
  outlinedButton,
  statValue,
  statLabel,
  loadingContainer,
  adminColors,
} from '../adminStyles'

interface UserProductReportData {
  alertProducts: Record<string, number>
  featureTags: Record<string, number>
  gtfStatuses: Record<string, number>
  greenchartStatuses: Record<string, number>
  totalUsers: number
}

interface CallableResponse {
  data: { data: UserProductReportData }
}

const getUserProductReportFunc = httpsCallable(firebase_functions, 'getUserProductReport')

function CountTable({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1])
  const total = entries.reduce((sum, [, count]) => sum + count, 0)

  return (
    <Box sx={sectionCard}>
      <Box sx={sectionHeader}>
        <Typography sx={sectionTitle}>{title}</Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadCell}>Name</TableCell>
              <TableCell align="right" sx={tableHeadCell}>
                Count
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([name, count]) => (
              <TableRow key={name} hover>
                <TableCell sx={tableBodyCell}>{name}</TableCell>
                <TableCell align="right" sx={tableBodyCell}>
                  {count.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{
                  ...tableBodyCell,
                  fontWeight: 600,
                  borderTop: `2px solid ${adminColors.border}`,
                }}
              >
                Total
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...tableBodyCell,
                  fontWeight: 600,
                  borderTop: `2px solid ${adminColors.border}`,
                }}
              >
                {total.toLocaleString()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default function UserProductReport() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reportData, setReportData] = useState<UserProductReportData | null>(null)

  const fetchReport = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = (await getUserProductReportFunc({})) as CallableResponse
      if (result?.data?.data) {
        setReportData(result.data.data)
      } else {
        throw new Error('Invalid response format')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch report.')
      setReportData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  return (
    <Box sx={pageContainer}>
      <Box sx={pageHeader}>
        <Box>
          <Typography sx={pageTitle}>User Product Report</Typography>
          {reportData && (
            <Typography sx={pageSubtitle}>
              {reportData.totalUsers.toLocaleString()} total users
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={fetchReport}
          disabled={loading}
          sx={outlinedButton}
        >
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box sx={loadingContainer}>
          <CircularProgress size={28} sx={{ color: adminColors.accent }} />
        </Box>
      )}

      {error && !loading && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchReport}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!loading && !error && reportData && (
        <>
          {/* Summary stat */}
          <Box sx={{ ...sectionCard, px: 2.5, py: 2 }}>
            <Typography sx={statValue}>{reportData.totalUsers.toLocaleString()}</Typography>
            <Typography sx={statLabel}>Total Users</Typography>
          </Box>

          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}
          >
            <CountTable title="Alert Products" data={reportData.alertProducts} />
            <CountTable title="Feature Tags" data={reportData.featureTags} />
            <CountTable title="GTF Status" data={reportData.gtfStatuses} />
            <CountTable title="GreenChart Status" data={reportData.greenchartStatuses} />
          </Box>
        </>
      )}
    </Box>
  )
}
